/**
 * Removable badges for applied Patient Notification filters.
 */

import {
  FILTER_LABELS,
  type NotificationFilters,
  type NotificationMultiSelectKey,
} from "../../../utils/notificationFilters";

/** Inputs for rendering and mutating applied filter badges. */
interface FilterBadgesProps {
  filters: NotificationFilters;
  onChange: (filters: NotificationFilters) => void;
  onReset: () => void;
}

const MULTI_SELECT_KEYS: NotificationMultiSelectKey[] = [
  "diagnosis",
  "stage",
  "insurance",
  "signoff",
  "patientTags",
];

/**
 * Format an ISO date for a compact filter badge.
 *
 * @param value - ISO date text
 * @returns Locale-formatted month/day/year
 */
function formatDate(value: string): string {
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

/**
 * Render each selected value as an independently removable badge.
 *
 * Renders nothing when no filters are applied, so the "Reset all" action only
 * appears alongside active filter badges.
 *
 * @param props - Applied filters and update callbacks
 * @returns Active filter badges and Reset All action, or null when no filters are applied
 */
export function FilterBadges({
  filters,
  onChange,
  onReset,
}: FilterBadgesProps) {
  /**
   * Remove one value while preserving the dimension's remaining selections.
   *
   * @param key - Multi-select filter dimension
   * @param value - Selected value to remove
   * @returns Nothing
   */
  const removeValue = (key: NotificationMultiSelectKey, value: string) => {
    const remaining = (filters[key] ?? []).filter((item) => item !== value);
    onChange({ ...filters, [key]: remaining.length ? remaining : undefined });
  };

  const appointment = filters.nextAppointment;

  // Only surface the badges row (and its "Reset all" action) once at least one
  // filter is applied — an empty filter set has nothing to reset.
  const hasActiveFilters =
    MULTI_SELECT_KEYS.some((key) => (filters[key] ?? []).length > 0) ||
    Boolean(appointment);
  if (!hasActiveFilters) return null;

  const appointmentLabel = appointment
    ? [
        appointment.from ? `From ${formatDate(appointment.from)}` : "",
        appointment.to ? `To ${formatDate(appointment.to)}` : "",
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  return (
    <div className="mb-4 flex flex-wrap gap-2">
      {MULTI_SELECT_KEYS.map((key) => {
        const values = filters[key] ?? [];
        if (!values.length) return null;
        return (
          <span
            key={key}
            className="inline-flex items-center gap-2 rounded-md bg-[var(--color-muted)] px-3 py-1 text-sm text-[var(--color-muted-foreground)]"
          >
            <span className="font-medium">{FILTER_LABELS[key]}:</span>
            {values.map((value) => (
              <span key={value} className="inline-flex items-center gap-1">
                {value}
                <button
                  type="button"
                  onClick={() => removeValue(key, value)}
                  aria-label={`Remove ${FILTER_LABELS[key]} ${value}`}
                >
                  ×
                </button>
              </span>
            ))}
          </span>
        );
      })}
      {appointment && (
        <span className="inline-flex items-center gap-2 rounded-md bg-[var(--color-muted)] px-3 py-1 text-sm text-[var(--color-muted-foreground)]">
          {FILTER_LABELS.nextAppointment}: {appointmentLabel}
          <button
            type="button"
            onClick={() => onChange({ ...filters, nextAppointment: undefined })}
            aria-label="Remove Next Appointment filter"
          >
            ×
          </button>
        </span>
      )}
      <button
        type="button"
        onClick={onReset}
        className="px-3 py-1 text-sm underline text-[var(--color-text-secondary)]"
      >
        Reset all
      </button>
    </div>
  );
}
